/**
 * Game Management Component
 * Admin view for creating, editing, and deleting games.
 */

import { EmptyState } from '../../components/empty-state';
import { SearchInput } from '../../components/search-input';
import { Modal } from '../../components/modal';
import { Table } from '../../components/table';
import { CheckRow, Checkbox, FormField, FormMessage, Input, Select } from '../../components/form-field';
import { saveFailure, loadFailure, deleteFailure, type FormFailure } from './save-failure-words';
import { activeTone } from './status-tone';
import { Pill } from '../../components/pill';
import { useState } from 'react';
import { Button } from '../../components/button';
import { useTranslation } from 'react-i18next';
import { CommonHeader } from '../shell/common-header';
import type { GameItem, CreateGamePayload, UpdateGamePayload } from '../../api/admin/games/types';
import {
  useAdminGames,
  useCreateGame,
  useUpdateGame,
  useDeleteGame,
} from '../../api/admin/games/hooks';
import { confirmDialog } from '../../components/confirm-dialog/confirm-dialog-store';
import admin from './admin-dashboard.module.css';

interface GameManagementProps {
  onBack: () => void;
}

export function GameManagement({ onBack }: GameManagementProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const gamesQuery = useAdminGames(page, 20);
  const deleteGame = useDeleteGame();
  const games: GameItem[] = gamesQuery.data?.data ?? [];
  const total = gamesQuery.data?.total ?? 0;
  const totalPages = gamesQuery.data?.totalPages ?? 1;
  const isLoading = gamesQuery.isLoading;
  const [pageError, setPageError] = useState<FormFailure | null>(null);
  const queryError = gamesQuery.isError
    ? loadFailure(t, gamesQuery.error, 'games')
    : null;
  const error = pageError ?? queryError;
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGame, setEditingGame] = useState<GameItem | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSaved = () => {
    setShowCreateForm(false);
    setEditingGame(null);
  };

  const handleDelete = async (game: GameItem) => {
    if (!(await confirmDialog({ message: t('admin.games.confirmDelete', { name: game.name }), tone: 'danger' }))) return;
    setPageError(null);
    try {
      await deleteGame.mutateAsync(game.id);
    } catch (e) {
      setPageError(deleteFailure(t, e, 'game'));
    }
  };

  const q = searchText.toLowerCase();
  const filteredGames = games.filter((g) => {
    const matchesSearch = !q ||
      g.name.toLowerCase().includes(q) ||
      (g.description ?? '').toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === '' ||
      (statusFilter === 'active' && g.isActive) ||
      (statusFilter === 'inactive' && !g.isActive);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="portal-shell">
      <CommonHeader
        title={t('admin.games.title')}
        showBackButton
        onBackClick={onBack}
      />

      <main className={admin.adminPanelMain}>
        {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}

        <div className={admin.adminTableWrapper}>
          <div className={admin.adminTableHeader}>
            <h2 className={admin.adminTableTitle}>
              {t('admin.games.totalCount', { count: total })}
            </h2>
            <div className={admin.adminFilterBar}>
              <Select
                className={admin.adminFilter}
                aria-label={t('common.filter')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as '' | 'active' | 'inactive')}
              >
                <option value="">{t('common.filterAll')}</option>
                <option value="active">{t('common.filterActive')}</option>
                <option value="inactive">{t('common.filterInactive')}</option>
              </Select>
              <Button type="button" onClick={() => setShowCreateForm(true)}
              >
                + {t('admin.games.createGame')}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className={admin.adminLoading}>{t('common.loading')}</div>
          ) : games.length === 0 ? (
            !error && <EmptyState icon="🎮">{t('admin.games.noGames')}</EmptyState>
          ) : (
            <>
              <div className={admin.adminSearchBar}>
                <SearchInput
            placeholder={t('common.searchPlaceholder')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
                {searchText && (
                  <span className={admin.adminSearchResults}>
                    {t('common.searchResults', { count: filteredGames.length, total: games.length })}
                  </span>
                )}
              </div>
              <Table flush>
              <thead>
                <tr>
                  <th>{t('admin.games.columns.name')}</th>
                  <th>{t('admin.games.columns.description')}</th>
                  <th>{t('admin.games.columns.worlds')}</th>
                  <th>{t('admin.games.columns.status')}</th>
                  <th>{t('admin.games.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredGames.map((game) => (
                  <tr key={game.id}>
                    <td>
                      <div className={`font-medium ${admin.adminTextStrong}`}>{game.name}</div>
                    </td>
                    <td className={admin.adminTextMuted}>{game.description ?? '—'}</td>
                    <td>{game.worldsCount ?? 0}</td>
                    <td>
                      <Pill tone={activeTone(game.isActive)}>
                        {game.isActive ? t('admin.games.active') : t('admin.games.inactive')}
                      </Pill>
                    </td>
                    <td>
                      <div className={admin.adminRowActions}>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditingGame(game)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button type="button" variant="dangerQuiet" size="sm" onClick={() => void handleDelete(game)}
                        >
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </>
          )}

          {totalPages > 1 && (
            <div className={admin.adminPagination}>
              <span>{t('admin.pagination.showing', { page, totalPages })}</span>
              <div className={admin.adminPaginationButtons}>
                <Button type="button" variant="ghost" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}
                >
                  {t('admin.pagination.prev')}
                </Button>
                <Button type="button" variant="ghost" size="sm" disabled={page>= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                >
                  {t('admin.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {showCreateForm && (
        <GameForm
          onClose={() => setShowCreateForm(false)}
          onSaved={handleSaved}
        />
      )}

      {editingGame && (
        <GameForm
          game={editingGame}
          onClose={() => setEditingGame(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ── Game form (create / edit) ─────────────────────────────────────────────────

interface GameFormProps {
  game?: GameItem;
  onClose: () => void;
  onSaved: () => void;
}

function GameForm({ game, onClose, onSaved }: GameFormProps) {
  const { t } = useTranslation();
  const isEditing = !!game;
  const createGame = useCreateGame();
  const updateGame = useUpdateGame();
  const isSubmitting = createGame.isPending || updateGame.isPending;

  const [name, setName] = useState(game?.name ?? '');
  const [description, setDescription] = useState(game?.description ?? '');
  const [isActive, setIsActive] = useState(game?.isActive ?? true);
  const [error, setError] = useState<FormFailure | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEditing && game) {
        const payload: UpdateGamePayload = {
          name,
          description: description || undefined,
          isActive,
        };
        await updateGame.mutateAsync({ id: game.id, payload });
      } else {
        const payload: CreateGamePayload = {
          name,
          description: description || undefined,
          isActive,
        };
        await createGame.mutateAsync(payload);
      }
      onSaved();
    } catch (e) {
      setError(saveFailure(t, e, 'game'));
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="mdWide" disableBackdropClose title={isEditing ? t('admin.games.editGame') : t('admin.games.createGame')}>
      {error && <FormMessage title={error.title}>{error.detail}</FormMessage>}
      <form onSubmit={handleSubmit}>
        <FormField label={t('admin.games.form.name')} required>
          {(field) => (
            <Input {...field}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ponder-flight-game"
              required
            />
          )}
        </FormField>
        <FormField label={t('admin.games.form.description')}>
          {(field) => (
            <Input {...field}
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </FormField>
        <CheckRow spaced>
          <Checkbox
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          {t('admin.games.form.active')}
        </CheckRow>
        <div className={admin.adminFormActions}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : (isEditing ? t('common.save') : t('common.create'))}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
